// React Query hooks for transaction drafts.
//
// All queries are scoped to the current cashier via `cashierId` so a
// cashier can only ever see their own drafts — both client-side and
// (defence-in-depth) server-side via Firestore rules.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listMyDrafts,
  saveDraft,
  deleteDraft,
  renameDraft,
  type SaveDraftInput,
} from '@/lib/api/drafts';

const KEYS = {
  all: ['transactionDrafts'] as const,
  myList: (cashierId: string) => [...KEYS.all, 'list', cashierId] as const,
};

export const draftKeys = KEYS;

/** List drafts for the given cashier (newest updated first). */
export const useMyDrafts = (cashierId: string | null | undefined) =>
  useQuery({
    queryKey: KEYS.myList(cashierId ?? ''),
    queryFn: () => (cashierId ? listMyDrafts(cashierId) : Promise.resolve([])),
    enabled: !!cashierId,
    // Drafts are a low-frequency, user-driven list — short stale time
    // is fine and means a freshly-saved draft shows up immediately.
    staleTime: 10 * 1000,
  });

export const useSaveDraft = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveDraftInput) => saveDraft(input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.myList(vars.cashierId ?? '') });
    },
  });
};

export const useDeleteDraft = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; cashierId: string }) => deleteDraft(vars.id),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.myList(vars.cashierId) });
    },
  });
};

export const useRenameDraft = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; label: string; cashierId: string }) =>
      renameDraft(vars.id, vars.label),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.myList(vars.cashierId) });
    },
  });
};
